import {
    Button,
    capitalize,
    Stack,
    Tooltip,
    Typography,
    Box,
    useTheme,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {CompetitiveGames, Maps, ScrimGames, Team} from "../../../reckon-api";
import {targets, useReferentialContext} from "../../../Context";
import {scrollable, getNeonColor} from "../../../theme";
import TeamIcon from "../../Icons/TeamIcon";
import AgentIcon from "../../Icons/AgentIcon";
import {MapBannerSelect} from "../MapSelect";
import {NeonCard, NeonCardContent, NeonCardHeader} from "../../../NeonComponents/NeonCards";
import {
    NeonTableHeader,
    NeonTableHeaderCell,
    NeonTableRow,
    NeonTableCell,
    NeonScoreBadge,
    NeonResultBadge,
    NeonEmptyState
} from "../../../NeonComponents/NeonTable";

export type SelectableMatchGame = CompetitiveGames | ScrimGames;

// Column configuration - using flex for responsive layout
const COLUMNS = {
    date: { flex: 0.8, minWidth: '50px' },
    league: { flex: 1.8, minWidth: '100px' },
    map: { flex: 0.8, minWidth: '55px' },
    opponent: { flex: 1.5, minWidth: '100px' },
    composition: { flex: 2, minWidth: '140px' },
    score: { flex: 1.5, minWidth: '110px' },
    result: { flex: 0.4, minWidth: '30px' },
} as const;

export default function SpecificGameSelector(props: { 
    team: Team; 
    updateSelection: any; 
    paramObject: any; 
    single?: boolean; 
    sameMap?: boolean;
    target?: targets;
}) {
    const ctx = useReferentialContext();
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    const default_map = ctx.mapsReferential.filter(m => m.id === 'lotus')[0]
    const [allGames, setAllGames] = React.useState([] as SelectableMatchGame[]);
    const [selectedIndexes, setSelectedIndex] = React.useState([] as boolean[]);
    const [map, setMap] = useState(props.sameMap ? default_map : null as Maps | null);
    const api = props.target === targets.scrims
        ? ctx.reckonApi.scrimGames
        : ctx.reckonApi.competitiveGames;

    const mapsWithGames = useMemo(
        () => [...new Set(allGames.map((g) => g.map).filter(Boolean))],
        [allGames]
    );

    const games = useMemo(
        () => (map ? allGames.filter((g) => g.map === map.id) : allGames),
        [allGames, map]
    );

    const handleChange = (clickedIndex: number) => {
        if(props.single){
            const nsi = games.map(() => false);
            nsi[clickedIndex] = true;
            props.updateSelection(games[clickedIndex]);
            setSelectedIndex(nsi);
        } else {
            const nsi = games.map((_, i) => selectedIndexes[i] ? true : false);
            nsi[clickedIndex] = !nsi[clickedIndex];
            props.updateSelection(games.filter((_, i) => nsi[i]));
            setSelectedIndex(nsi);
        }
    };

    const getSeasonIndexes = (year: number) =>
        games
            .map((game, i) => (game.date && dayjs(game.date).year() === year ? i : -1))
            .filter((i) => i !== -1);

    const isSeasonFullySelected = (year: number) => {
        const yearIndexes = getSeasonIndexes(year);
        return yearIndexes.length > 0 && yearIndexes.every((i) => selectedIndexes[i]);
    };

    const handleSelectSeason = (year: number) => {
        if (props.single) return;
        const yearIndexes = getSeasonIndexes(year);
        const allSelected = yearIndexes.every((i) => selectedIndexes[i]);
        const nsi = games.map((_, i) => (selectedIndexes[i] ? true : false));
        yearIndexes.forEach((i) => {
            nsi[i] = !allSelected;
        });
        props.updateSelection(games.filter((_, i) => nsi[i]));
        setSelectedIndex(nsi);
    };

    useEffect(() => {
        setAllGames([]);
        const request_params = {...props.paramObject, ordering: ['-date','-id']}
        api.list(request_params).then(r => setAllGames(r))
    }, [props.paramObject.watermark, props.target]);

    useEffect(() => {
        if (!mapsWithGames.length) return;
        if (map && mapsWithGames.includes(map.id)) return;
        if (!props.sameMap && map === null) return;
        const next = ctx.mapsReferential.find((m) => mapsWithGames.includes(m.id));
        if (next) setMap(next);
    }, [mapsWithGames, map, props.sameMap, ctx.mapsReferential]);

    useEffect(() => {
        setSelectedIndex(games.map(() => false));
        if (props.single) {
            props.updateSelection(null);
        } else {
            props.updateSelection([]);
        }
    }, [map, allGames]);

    return (
        <NeonCard
            sx={{
                display: "flex",
                height: "100%",
                width: "100%",
                flexDirection: 'column',
            }}
        >
            <NeonCardHeader 
                subheader={
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>
                        {games.length} games
                    </Typography>
                }
                title={
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <TeamIcon team_name={props.team.name} sx={{ width: 28, height: 28 }}/>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                            Match History
                        </Typography>
                    </Stack>
                }
            />
            <NeonCardContent sx={{ 
                flex: 1,
                minHeight: 0,
                width: '100%', 
                padding: '8px 12px 16px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                <Box sx={{ mb: 1.25, flexShrink: 0 }}>
                    <MapBannerSelect
                        value={map}
                        updateValue={setMap}
                        allowClear={!props.sameMap}
                        height={40}
                        mapsWithGames={mapsWithGames}
                    />
                </Box>
                {/* Header */}
                <NeonTableHeader>
                    <NeonTableHeaderCell flex={COLUMNS.date.flex} minWidth={COLUMNS.date.minWidth} align="left">Date</NeonTableHeaderCell>
                    <NeonTableHeaderCell flex={COLUMNS.league.flex} minWidth={COLUMNS.league.minWidth} align="left">League</NeonTableHeaderCell>
                    <NeonTableHeaderCell flex={COLUMNS.map.flex} minWidth={COLUMNS.map.minWidth} align="left">Map</NeonTableHeaderCell>
                    <NeonTableHeaderCell flex={COLUMNS.opponent.flex} minWidth={COLUMNS.opponent.minWidth} align="left">Opponent</NeonTableHeaderCell>
                    <NeonTableHeaderCell flex={COLUMNS.composition.flex} minWidth={COLUMNS.composition.minWidth} align="center">Comp</NeonTableHeaderCell>
                    <NeonTableHeaderCell flex={COLUMNS.score.flex} minWidth={COLUMNS.score.minWidth} align="left">Score</NeonTableHeaderCell>
                    <NeonTableHeaderCell flex={COLUMNS.result.flex} minWidth={COLUMNS.result.minWidth} align="center"></NeonTableHeaderCell>
                </NeonTableHeader>

                {/* Games list */}
                <Box sx={{ 
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    ...scrollable,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    paddingRight: '4px',
                }}>
                        {games.map((game: SelectableMatchGame, index: number) => {
                            const isWin = game.winner === props.team.id;
                            const isTeam1 = props.team.id === game.team1;
                            const teamAgents = isTeam1 ? game.team1_agents : game.team2_agents;
                            const teamScore = isTeam1 ? game.score_team1 : game.score_team2;
                            const opponentScore = isTeam1 ? game.score_team2 : game.score_team1;
                            const opponentId = isTeam1 ? game.team2 : game.team1;
                            const roundDiff = teamScore - opponentScore;
                            const opponentTeam = ctx.teamsReferential.find(t => t.id === opponentId);
                            const opponentName = opponentTeam?.name || opponentId;
                            const leagueLabel = 'league' in game && game.league ? game.league : (props.target === targets.scrims ? 'Scrim' : '');
                            const phaseLabel = 'phase' in game && game.phase ? game.phase : '';
                            const splitLabel = 'split' in game && game.split ? game.split : '';

                            // Check if we need a year divider
                            const gameYear = game.date ? dayjs(game.date).year() : null;
                            const prevGame = index > 0 ? games[index - 1] : null;
                            const prevYear = prevGame?.date ? dayjs(prevGame.date).year() : null;
                            const showYearDivider = index === 0 || (gameYear !== null && gameYear !== prevYear);
                            const allSeasonSelected = gameYear !== null && isSeasonFullySelected(gameYear);

                            return (
                                <React.Fragment key={game.id || index}>
                                    {showYearDivider && gameYear && (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            padding: '10px 16px',
                                            marginTop: index === 0 ? 0 : '8px',
                                            marginBottom: '4px',
                                            borderRadius: '6px',
                                            background: `linear-gradient(90deg, ${neonColor}12 0%, ${neonColor}06 30%, rgba(255,255,255,0.02) 100%)`,
                                            borderLeft: `3px solid ${neonColor}`,
                                            boxShadow: `inset 0 0 20px ${neonColor}08`,
                                        }}>
                                            <Typography sx={{
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                color: 'rgba(255,255,255,0.9)',
                                                letterSpacing: '1px',
                                            }}>
                                                {gameYear} Season
                                            </Typography>
                                            {!props.single && (
                                                <Button
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSelectSeason(gameYear);
                                                    }}
                                                    sx={{
                                                        minWidth: 0,
                                                        padding: '2px 8px',
                                                        fontSize: '0.65rem',
                                                        fontWeight: 600,
                                                        letterSpacing: '0.5px',
                                                        textTransform: 'none',
                                                        color: allSeasonSelected ? 'rgba(255,255,255,0.7)' : neonColor,
                                                        border: `1px solid ${allSeasonSelected ? 'rgba(255,255,255,0.25)' : `${neonColor}66`}`,
                                                        background: allSeasonSelected ? 'rgba(255,255,255,0.06)' : `${neonColor}14`,
                                                        '&:hover': {
                                                            background: allSeasonSelected ? 'rgba(255,255,255,0.1)' : `${neonColor}28`,
                                                            borderColor: allSeasonSelected ? 'rgba(255,255,255,0.4)' : neonColor,
                                                        },
                                                    }}
                                                >
                                                    {allSeasonSelected ? 'Deselect all' : 'Select all'}
                                                </Button>
                                            )}
                                        </Box>
                                    )}
                                <Tooltip
                                    title={
                                        <Stack spacing={0.5} sx={{ padding: '4px' }}>
                                            <Typography variant="body2" fontWeight={600}>{leagueLabel}</Typography>
                                            <Typography variant="caption">
                                                {[phaseLabel, splitLabel].filter(Boolean).join(' • ')}
                                            </Typography>
                                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                                {game.date ? dayjs(game.date).format("MMMM D, YYYY") : ''}
                                            </Typography>
                                        </Stack>
                                    }
                                    placement="left"
                                    arrow
                                >
                                    <Box>
                                        <NeonTableRow
                                            onClick={() => handleChange(index)}
                                            isSelected={selectedIndexes[index] === true}
                                            variant={isWin ? 'success' : 'error'}
                                            animationDelay={index}
                                        >
                                            {/* Date */}
                                            <NeonTableCell flex={COLUMNS.date.flex} minWidth={COLUMNS.date.minWidth} align="left">
                                                <Typography 
                                                    sx={{ 
                                                        fontSize: '0.65rem',
                                                        color: 'rgba(255,255,255,0.5)',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {game.date ? dayjs(game.date).format(
                                                        dayjs(game.date).year() === dayjs().year() ? "MMM DD" : "MMM 'YY"
                                                    ) : ''}
                                                </Typography>
                                            </NeonTableCell>

                                            {/* League/Event */}
                                            <NeonTableCell flex={COLUMNS.league.flex} minWidth={COLUMNS.league.minWidth} align="left">
                                                <Stack spacing={0} alignItems="flex-start">
                                                    <Typography 
                                                        sx={{ 
                                                            fontSize: '0.6rem',
                                                            fontWeight: 600,
                                                            color: 'rgba(255,255,255,0.85)',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            maxWidth: '100%',
                                                        }}
                                                    >
                                                        {leagueLabel}
                                                    </Typography>
                                                    <Typography 
                                                        sx={{ 
                                                            fontSize: '0.5rem',
                                                            color: 'rgba(255,255,255,0.4)',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            maxWidth: '100%',
                                                        }}
                                                    >
                                                        {phaseLabel}{splitLabel ? ` • ${splitLabel}` : ''}
                                                    </Typography>
                                                </Stack>
                                            </NeonTableCell>

                                            {/* Map */}
                                            <NeonTableCell flex={COLUMNS.map.flex} minWidth={COLUMNS.map.minWidth} align="left">
                                                <Typography 
                                                    sx={{ 
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600,
                                                        color: 'rgba(255,255,255,0.85)',
                                                    }}
                                                >
                                                    {capitalize(game.map)}
                                                </Typography>
                                            </NeonTableCell>

                                            {/* Opponent */}
                                            <NeonTableCell flex={COLUMNS.opponent.flex} minWidth={COLUMNS.opponent.minWidth} align="left">
                                                <Stack direction="row" spacing={0.75} alignItems="center">
                                                    <TeamIcon 
                                                        team_name={opponentId} 
                                                        sx={{ width: 22, height: 22, flexShrink: 0 }}
                                                    />
                                                    <Typography sx={{ 
                                                        fontSize: '0.65rem',
                                                        fontWeight: 500,
                                                        color: 'rgba(255,255,255,0.7)',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        {opponentName}
                                                    </Typography>
                                                </Stack>
                                            </NeonTableCell>

                                            {/* Composition */}
                                            <NeonTableCell flex={COLUMNS.composition.flex} minWidth={COLUMNS.composition.minWidth} align="center">
                                                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                                    {teamAgents.split(',').map((agentName: string, i: number) => (
                                                        <AgentIcon 
                                                            key={i} 
                                                            agent_name={agentName} 
                                                            sx={{ width: 24, height: 24 }}
                                                        />
                                                    ))}
                                                </Stack>
                                            </NeonTableCell>

                                            {/* Score with round diff */}
                                            <NeonTableCell flex={COLUMNS.score.flex} minWidth={COLUMNS.score.minWidth} align="left">
                                                <Stack direction="row" spacing={0.75} alignItems="center">
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <NeonScoreBadge 
                                                            score={teamScore} 
                                                            variant={isWin ? 'win' : 'loss'}
                                                            size="small"
                                                        />
                                                        <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.65rem' }}>-</Typography>
                                                        <NeonScoreBadge 
                                                            score={opponentScore} 
                                                            variant={!isWin ? 'win' : 'loss'}
                                                            size="small"
                                                        />
                                                    </Stack>
                                                    <Typography sx={{ 
                                                        fontSize: '0.6rem',
                                                        fontWeight: 700,
                                                        color: roundDiff > 0 ? '#2ecc71' : '#e74c3c',
                                                        minWidth: '28px',
                                                    }}>
                                                        {roundDiff > 0 ? `+${roundDiff}` : roundDiff}
                                                    </Typography>
                                                </Stack>
                                            </NeonTableCell>

                                            {/* Result */}
                                            <NeonTableCell flex={COLUMNS.result.flex} minWidth={COLUMNS.result.minWidth} align="center">
                                                <NeonResultBadge result={isWin ? 'W' : 'L'} />
                                            </NeonTableCell>
                                        </NeonTableRow>
                                    </Box>
                                </Tooltip>
                                </React.Fragment>
                            );
                        })}

                        {games.length === 0 && (
                            <NeonEmptyState message="No games found" />
                        )}
                    </Box>
            </NeonCardContent>
        </NeonCard>
    );
}
